import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  GameSessionFilters,
  getGameSessionStats,
  GameSessionStats,
  addGameSession,
  updateGameSession,
  deleteGameSession,
  getAllGameSessions, // Usado para buscar sessões próprias e compartilhadas
  CombinedGameSession,
  GameSessionFromDB,
  shareGameSession,
  Player,
  GameSession
} from '../../../lib/game/sessionService';
import { UserGame } from '../../../lib/game/collectionService';
import toast from 'react-hot-toast';

const PAGE_SIZE = 20;

interface UseGameSessionsProps {
  initialPage?: number;
  initialFilters?: GameSessionFilters;
}

export const useGameSessions = ({
  initialPage = 1,
  initialFilters = {},
}: UseGameSessionsProps = {}) => {
  const { user } = useAuth();
  const [gameSessions, setGameSessions] = useState<CombinedGameSession[]>([]);
  const [sessionsCount, setSessionsCount] = useState(0);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsPage, setSessionsPage] = useState(initialPage);
  const [sessionsFilters, setSessionsFilters] = useState<GameSessionFilters>(initialFilters);
  const [error, setError] = useState<string | null>(null);
  const [gameSessionStats, setGameSessionStats] = useState<{ [gameId: string]: GameSessionStats }>({});

  const loadGameSessions = useCallback(async () => {
    if (!user?.id) {
      setGameSessions([]);
      setSessionsCount(0);
      setSessionsLoading(false);
      return;
    }

    setSessionsLoading(true);
    setError(null);
    console.log('[HOOK_SESSIONS] Carregando sessões de jogo com filtros...', { sessionsFilters, sessionsPage });

    try {
      const offset = (sessionsPage - 1) * PAGE_SIZE;
      // Busca sessões passando os filtros e recebe dados + contagem
      const { data: sessions, count } = await getAllGameSessions(
        user.id,
        sessionsFilters, // Passa os filtros
        PAGE_SIZE,
        offset
      );

      // Validação e sanitização dos dados recebidos
      // Garantir que todos os objetos de sessão tenham a propriedade is_owner como booleano
      const sanitizedSessions = sessions.map(session => {
        // Converter explicitamente is_owner para booleano
        const sanitizedSession = {
          ...session,
          is_owner: Boolean(session.is_owner), // Conversão simples para booleano, false permanece false
        };
        return sanitizedSession;
      });

      // <<< LOG 1: Verificar dados brutos da API >>>
      console.log('[useGameSessions] Raw data received from getAllGameSessions:', { sessions: sanitizedSessions, count });
      
      // Verificar especificamente sessões compartilhadas nos dados brutos
      const sharedSessions = sanitizedSessions.filter(s => s.is_owner === false);
      console.log('[useGameSessions] Sessões compartilhadas nos dados brutos:', sharedSessions.length, 
        sharedSessions.map(s => ({ 
          id: s.id, 
          game_name: s.game_name, 
          game_slug: s.game_slug, 
          is_owner: s.is_owner,
          tipo_is_owner: typeof s.is_owner 
        })));

      // Verificação adicional de tipos e valores exatos
      if (sanitizedSessions.length > 0) {
        const firstSession = sanitizedSessions[0];
        console.log('[useGameSessions] Tipo de is_owner:', typeof firstSession.is_owner);
        console.log('[useGameSessions] Valor exato de is_owner:', firstSession.is_owner);
        console.log('[useGameSessions] Exemplo completo de sessão antes do mapeamento:', JSON.stringify(firstSession));
      }

      // <<< LOG 1.5: Log sessions array *just before* map >>>
      console.log('[useGameSessions] sessions array before .map():', sanitizedSessions);

      // Mapeamento dos dados
      const mappedSessionsOrNulls = sanitizedSessions.map((session: GameSessionFromDB): CombinedGameSession | null => {
        try {
          // Mapeia os campos de GameSessionFromDB para CombinedGameSession
          // Define defaults/null para campos não retornados pela RPC específica
          const isOwner = Boolean(session.is_owner); // Conversão direta para booleano
          console.log(`[useGameSessions] Mapeando sessão ${session.id}, is_owner original: ${session.is_owner}, convertido: ${isOwner}, tipo original: ${typeof session.is_owner}`);
          
          return {
            // Campos obrigatórios de GameSession (alguns podem vir da RPC, outros default)
            id: session.id,
            user_id: session.owner_id, // Na CombinedGameSession, user_id refere-se ao owner
            game_id: session.game_id,
            played_at: session.played_at,
            duration_minutes: session.duration_minutes,
            player_count: session.player_count,
            location: null, // Não disponível em GameSessionFromDB
            result: session.result,
            score: session.score,
            opponent_score: null, // Não disponível em GameSessionFromDB
            notes: null, // Não disponível em GameSessionFromDB
            players: session.players as Player[] | null, // Mapeando o campo players
            created_at: new Date().toISOString(), // Placeholder - Não disponível em GameSessionFromDB
            updated_at: new Date().toISOString(), // Placeholder - Não disponível em GameSessionFromDB

            // Objeto 'game' opcional (construído a partir dos campos da RPC)
            game: session.game_slug ? {
              id: session.game_id,
              name: session.game_name,
              image_url: session.game_image,
              thumb_url: session.game_image, // Usando image como fallback para thumb
              slug: session.game_slug, // Usa o slug real se estiver disponível
            } : {
              id: session.game_id,
              name: session.game_name,
              image_url: session.game_image,
              thumb_url: session.game_image,
              slug: `game-${session.game_id}`, // Fallback para evitar undefined
            },

            // Campos específicos de CombinedGameSession
            owner_id: session.owner_id,
            is_owner: isOwner, // Usar o valor convertido para booleano
            owner_username: session.owner_username, // Adicionado para popular a partir da RPC
            share_status: session.share_status as CombinedGameSession['share_status'] // Mapeia o status de compartilhamento
          };
        } catch (mapError) {
          console.error(`[useGameSessions] Error mapping session ID ${session?.id}:`, mapError, session);
          return null; // Retorna null se houver erro no mapeamento deste item
        }
      });

      // <<< LOG 2: Verificar dados após mapeamento, *before* filtering nulls >>>
      console.log('[useGameSessions] Data after mapping (mappedSessionsOrNulls):', mappedSessionsOrNulls);

      // Filtra os nulos resultantes de erros
      const formattedSessions = mappedSessionsOrNulls.filter((s): s is CombinedGameSession => s !== null);

      // <<< LOG 3: Verificar dados *after* filtering nulls >>>
      console.log('[useGameSessions] Data after filtering nulls (formattedSessions):', formattedSessions);

      setGameSessions(formattedSessions); // Define diretamente os dados formatados
      setSessionsCount(count); // Define a contagem total real vinda da API

    } catch (err) {
      console.error('[HOOK_SESSIONS] Erro ao carregar sessões de jogo:', err);
      setError('Não foi possível carregar o histórico de partidas. Tente novamente.');
      setGameSessions([]);
      setSessionsCount(0);
    } finally {
      setSessionsLoading(false);
    }
  }, [user?.id, sessionsFilters, sessionsPage]);

  const loadGameSessionStats = useCallback(async (gameId: string) => {
    if (!user?.id || !gameId) return;
    try {
      const stats = await getGameSessionStats(user.id, gameId);
      if (stats) {
        setGameSessionStats(prev => ({
          ...prev,
          [gameId]: stats
        }));
      }
    } catch (error) {
      console.error(`[HOOK_SESSIONS] Erro ao carregar estatísticas para jogo ${gameId}:`, error);
      // Pode-se definir um erro específico para estatísticas se necessário
    }
  }, [user?.id]);

  useEffect(() => {
    loadGameSessions();
  }, [loadGameSessions]);

  // Handlers
  const handleSessionFilter = (newFilters: GameSessionFilters) => {
    setSessionsFilters(prevFilters => {
        // Só atualiza se os filtros realmente mudaram
        if (JSON.stringify(prevFilters) !== JSON.stringify(newFilters)) {
            setSessionsPage(1); // Reset page when filters change
            return newFilters;
        }
        return prevFilters; // Mantém os filtros antigos se nada mudou
    });
  };

  const handleSessionsPageChange = (newPage: number) => {
    // Só atualiza se a página realmente mudou e é válida
    if (newPage !== sessionsPage && newPage > 0) {
      setSessionsPage(newPage);
    }
  };

  const handleDeleteSession = async (sessionId: string) => {
    if (!user?.id) return false;
    // Confirmação pode ser movida para o componente que chama
    try {
      const success = await deleteGameSession(sessionId);
      if (success) {
        toast.success('Partida excluída com sucesso!');
        // Recarrega os dados da página atual após exclusão
        loadGameSessions();
        // Se precisar recarregar stats de um jogo específico, precisaria do gameId
        return true;
      } else {
        setError('Não foi possível excluir a partida. Tente novamente.');
        toast.error('Erro ao excluir a partida.');
        return false;
      }
    } catch (err) {
      console.error('[HOOK_SESSIONS] Erro ao excluir sessão:', err);
      setError('Não foi possível excluir a partida. Tente novamente.');
      toast.error('Erro ao excluir a partida.');
      return false;
    }
  };

  return {
    gameSessions,
    sessionsCount,
    sessionsLoading,
    sessionsPage,
    sessionsFilters,
    error,
    gameSessionStats,
    sessionsTotalPages: Math.ceil(sessionsCount / PAGE_SIZE),
    loadGameSessions, // Exporta para recarregar manualmente se necessário
    loadGameSessionStats,
    handleSessionFilter,
    handleSessionsPageChange,
    handleDeleteSession,
    setGameSessionStats, // Permite atualizar stats externamente (ex: após salvar sessão)
  };
}; 